import React, { ReactElement, useCallback, useEffect, useState } from 'react'
import PageHeader from '../components/PageHeader'
import Table from '../components/Table'
import Radom, {
  // Customer,
  Payout
} from '../state/Radom'
import { observer } from 'mobx-react'
import { PrimaryButton } from '../components/Button'
import { formatCurrency, useTestMode, ROUTES } from '../util/Util'
import { Container } from '../components/Animations'
import Pagination from '../components/Pagination'
import PayoutModal from './Funds/PayoutModal'
import TimeText from '../components/TimeText'
import { StatusBadge } from './CheckoutSessions'
import { Link, useNavigate } from 'react-router-dom'
import Dropdown, { DropdownItem } from '../components/Dropdown'
import Checkbox from '../components/Checkbox'
import Dots from '../icons/Dots'
import ExportPayoutsForm from '../components/ExportPayoutsForm'
import Modal from '../components/Modal'
import { PayoutStatusFilter } from '../util/Constants'
// import { prodNetworks, testNetworks } from '../util/Managed'
import { payoutBadgeStatus, payoutStatusLabel, payoutStatusOptions } from '../util/PayoutStatus'
import {
  DropdownLabel,
  ListContent,
  ListControls,
  ListPage,
  ListPaginationRow,
  PageActionRow,
  PageHeaderRow,
  PageHeaderStack
} from '../components/DashboardPrimitives'
import Gate from '../components/Gate'

const Payouts = observer((): ReactElement => {
  const [isLoading, setIsLoading] = useState(false)
  const navigate = useNavigate()

  const pageSize = 10
  const [curPage, setCurPage] = useState(0)
  const [payouts, setPayouts] = useState<Payout[]>([])
  const [totalPayoutsSize, setTotalPayoutsSize] = useState(0)
  const [testMode] = useTestMode()
  // const [customers, setCustomers] = useState<Customer[]>([])
  const [selectedPayoutId, setSelectedPayoutId] = useState<string>()
  const [isPayoutModalOpen, setIsPayoutModalOpen] = useState(false)
  const [exportPayoutsModalOpen, setExportPayoutsModalOpen] = useState(false)

  const [payoutStatusFilter, setPayoutStatusFilter] = useState<PayoutStatusFilter[]>([

  ])

  const loadData = useCallback(async (): Promise<void> => {
    setIsLoading(true)
    setPayouts([])
    setTotalPayoutsSize(0)

    try {
      // const networks = testMode ? testNetworks : prodNetworks
      const [payoutsRes
        // customersRes
      ] = await Promise.all([
        Radom.listPayouts(curPage * pageSize, pageSize, testMode, payoutStatusFilter, false)
        // Radom.listCustomers(0, 100, networks)
      ])

      setPayouts(payoutsRes.data)
      setTotalPayoutsSize(payoutsRes.total)
      // setCustomers(customersRes.data)
    } catch (e) {
      console.error(e)
    } finally {
      setIsLoading(false)
    }
  }, [testMode, curPage, payoutStatusFilter])

  useEffect(() => {
    loadData()
  }, [loadData])

  return <Container>
    <ListPage>
      <PageHeaderRow>
        <PageHeaderStack>
          <PageHeader title='Payouts' subTitle='Manage and view your payouts' />
          <ListControls>
            <Dropdown
              selectedContent={<DropdownLabel>Payout status</DropdownLabel>}
              dropdownContent={<div>
                {payoutStatusOptions.map(s =>
                  <DropdownItem
                    key={s}
                    style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: 14 }}
                    onClick={() => {
                      if (payoutStatusFilter.includes(s)) {
                        setPayoutStatusFilter(payoutStatusFilter.filter(st => st !== s))
                      } else {
                        setPayoutStatusFilter([...payoutStatusFilter, s])
                      }
                    }}>
                    <Checkbox size={15} checked={payoutStatusFilter.includes(s)} />
                    <span>{payoutStatusLabel(s)}</span>
                  </DropdownItem>
                )}
              </div>} />
          </ListControls>
        </PageHeaderStack>
        <PageActionRow>
          {/* Matches the CREATE_PAYOUT route requirement (see App.tsx). */}
          <Gate permission={['WritePayouts', 'WriteConfirmPayouts', 'ReadCustomers', 'ReadExternalAccount']}>
            <Link to={ROUTES.CREATE_PAYOUT} style={{ textDecoration: 'none' }}>
              <PrimaryButton style={{ gap: 6 }}>
                <span style={{ fontSize: 12.5, lineHeight: 1, fontWeight: 500 }}>+</span>
                <span>Create payout</span>
              </PrimaryButton>
            </Link>
          </Gate>

          <Gate permission='ReadEvents'>
            <Dropdown
              noChevron
              iconTrigger
              selectedContent={<Dots width={15} />}
              dropdownContent={
                <div style={{ fontSize: 14, whiteSpace: 'nowrap' }}>
                  <DropdownItem onClick={() => setExportPayoutsModalOpen(true)}>Export payouts</DropdownItem>
                </div>
              }
            />
          </Gate>
        </PageActionRow>
      </PageHeaderRow>

      <ListContent>

        <Table
          borderless
          onClickRow={(i) => {
            if (payouts[i].status === 'Complete' || payouts[i].status === 'Failed') {
              navigate(ROUTES.getPayoutDetailRoute(payouts[i].id))
              return
            }
            setSelectedPayoutId(payouts[i].id)
            setIsPayoutModalOpen(true)
          }}
          headers={['Type', 'Beneficiary', 'Status', 'Amount', 'Fee', 'Completed', 'Created'].map(h => <span key={h}>{h}</span>)}
          items={
            payouts.map((p) => {
              const isFiat = p.fiatEntries && p.fiatEntries.length > 0
              const PAYOUT_TYPE = isFiat ? 'Fiat' : 'Crypto'
              const beneficiary = isFiat
                ? (p.fiatEntries[0]?.beneficiaryName || p.fiatEntries[0]?.beneficiaryEmail || '-')
                : (p.cryptoEntries?.[0]?.payoutAddress
                  ? `${p.cryptoEntries[0].payoutAddress.slice(0, 6)}...${p.cryptoEntries[0].payoutAddress.slice(-4)}`
                  : '-')

              const fundingCurrency = p.cryptoEntries?.[0]?.fundingCurrency
              const feeTicker = isFiat ? 'USDC' : (fundingCurrency || 'USDC')
              const fiatCurrency = isFiat ? (p.fiatEntries[0]?.currency || 'USD') : undefined
              const feeIsZero = !Number(p.estimatedFeeUsd)
              // Fiat rows show amounts in the payout currency while fees are
              // charged in USDC: render zero fees in the payout currency; a
              // nonzero fee already carries its own ticker.
              const feeContent = isFiat && fiatCurrency && feeIsZero
                ? formatCurrency(0, fiatCurrency)
                : formatCurrency(p.estimatedFeeUsd, feeTicker)

              return [
                <div key={`${p.id}-type`}>{PAYOUT_TYPE}</div>,
                <div key={`${p.id}-ben`}>{beneficiary}</div>,
                <div key={`${p.id}-status`}>
                  <StatusBadge status={payoutBadgeStatus(p.status)}>{payoutStatusLabel(p.status)}</StatusBadge>
                </div>,
                <div key={`${p.id}-amt`}>{
                  isFiat
                    ? formatCurrency(p.fiatEntries[0]?.amount || p.totalAmountUsd, p.fiatEntries[0]?.currency || 'USD')
                    : formatCurrency(p.totalAmountUsd, 'USD')
                }</div>,
                <div key={`${p.id}-fee`}>{feeContent}</div>,
                <div key={`${p.id}-comp`}>{p.completedAt ? <TimeText date={new Date(p.completedAt)} /> : '-'}</div>,
                <div key={`${p.id}-crt`}><TimeText date={new Date(p.createdAt)} /></div>
              ]
            })
          }
          isLoading={isLoading}
          emptyPlaceholderText='No payouts yet'
        />

        <ListPaginationRow>
          <Pagination
            curPage={curPage}
            numPages={Math.ceil(totalPayoutsSize / pageSize)}
            onPageClick={page => setCurPage(page)}
          />
        </ListPaginationRow>
      </ListContent>

    </ListPage>

    {
      exportPayoutsModalOpen &&
      <Modal visible={exportPayoutsModalOpen} onClose={() => setExportPayoutsModalOpen(false)} title='Export payouts'>
        <ExportPayoutsForm />
      </Modal>
    }

    {
      isPayoutModalOpen && selectedPayoutId &&
      <PayoutModal
        payoutId={selectedPayoutId}
        visible={isPayoutModalOpen}
        onPayoutConfirmed={async () => {
          await loadData()
          setIsPayoutModalOpen(false)
        }}
        onClose={() => setIsPayoutModalOpen(false)} />
    }
  </Container>
})

export default Payouts
